import React, { ComponentProps } from 'react';
import styled from 'styled-components';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Column } from 'primereact/column';
import { AdminCompanySearchResultItem } from 'types/admin-search-companies/AdminCompanySearchResultItem';
import AdminSearchCompaniesQueryHelper, { PAGE_SIZES } from './AdminSearchCompaniesQueryHelper';
import { useAdminLookup } from 'services/api/queries';
import { AdminLookupKeyEnum } from 'types/types';
import Tooltip from 'components/controls/Tooltip';
import AdminSearchResultsGrid from 'components/admin/search/AdminSearchResultsGrid';
import { EpdLinks } from 'routes/EpdRoutes';
import FontIcon from 'components/icons/FontIcon';
import { PagedResult } from 'types/search-common/PagedResult';

interface Props {
  data: PagedResult<AdminCompanySearchResultItem>;
  showLoading: boolean;
}

const AdminSearchCompaniesResultsTable: React.FC<Props> = ({ data: { totalCount, items }, showLoading }) => {
  const location = useLocation();
  const currentFilter = AdminSearchCompaniesQueryHelper.parse(location.search);
  const history = useHistory();
  const countries = useAdminLookup(AdminLookupKeyEnum.COUNTRIES).data!;
  const companyRoles = useAdminLookup(AdminLookupKeyEnum.COMPANY_ROLES).data!;

  const onPage: ComponentProps<typeof AdminSearchResultsGrid>['onPageChange'] = (event) => {
    const newQuery = AdminSearchCompaniesQueryHelper.stringify({
      ...currentFilter,
      pageNumber: event.pageIndex + 1,
      pageSize: event.pageSize,
    });
    history.push(location.pathname + newQuery);
    document.getElementById('searchResults')?.scrollIntoView();
  };

  const renderLink = (data: AdminCompanySearchResultItem, additionalInfo?: { tooltip?: string }) => (
    // We want the entire cell to be clickable (including padding areas).
    <OverlayEntireCell>
      <CellLink to={EpdLinks.editCompany(data.id)} aria-label={`See ${data.name}`} />
      {additionalInfo?.tooltip && <Tooltip position="title">{additionalInfo.tooltip}</Tooltip>}
    </OverlayEntireCell>
  );

  return (
    <AdminSearchResultsGrid
      id="searchResults"
      style={{ scrollMarginTop: 100 }}
      value={items}
      totalRecords={totalCount}
      pageIndex={currentFilter.pageNumber - 1}
      pageSize={currentFilter.pageSize}
      onPageChange={onPage}
      pageSizeOptions={PAGE_SIZES}
      loading={showLoading}
      tableStyle={{ tableLayout: 'fixed' }}
    >
      <Column
        field="name"
        header="Organisation"
        body={(data: AdminCompanySearchResultItem) => (
          <EllipsedCell>
            {renderLink(data, { tooltip: data.name })}

            <EllipsedText>{data.name || '-'}</EllipsedText>
            {data.isArchived && (
              <ArchivedCircle>
                <FontIcon icon="archive" />
                <Tooltip position="title">Archived</Tooltip>
              </ArchivedCircle>
            )}
          </EllipsedCell>
        )}
      />
      <Column
        field="countryId"
        header="Country"
        style={{ width: 140 }}
        body={(data: AdminCompanySearchResultItem) => {
          const country = !!data.countryId && countries.find((c) => c.value === data.countryId);
          if (!country) {
            return <>{renderLink(data)}-</>;
          }
          return (
            <EllipsedCell>
              {renderLink(data, { tooltip: country.label })}
              <EllipsedText>{country.label}</EllipsedText>
            </EllipsedCell>
          );
        }}
      />
      <Column
        field="size"
        header="Size"
        style={{ width: 200 }}
        body={(data: AdminCompanySearchResultItem) => (
          <EllipsedCell>
            {renderLink(data, { tooltip: data.size })}
            <EllipsedText>{data.size}</EllipsedText>
          </EllipsedCell>
        )}
      />
      <Column
        field="roleIds"
        header="Type"
        style={{ width: 160 }}
        body={(data: AdminCompanySearchResultItem) => {
          const roles = data.roleIds
            .map((roleId) => companyRoles.find((o) => o.value === roleId)?.label)
            .filter((x): x is string => !!x);
          return (
            <EllipsedCell>
              {renderLink(data, { tooltip: roles.join(', ') })}

              <EllipsedText>{roles[0] || '-'}</EllipsedText>
              {roles.length >= 2 && <PlusXMore>+{roles.length - 1}</PlusXMore>}
            </EllipsedCell>
          );
        }}
      />
      <Column
        field="structure"
        header="Structure"
        style={{ width: 120 }}
        body={(data: AdminCompanySearchResultItem) => (
          <EllipsedCell>
            {renderLink(data)}
            <EllipsedText>{data.structure}</EllipsedText>
          </EllipsedCell>
        )}
      />
    </AdminSearchResultsGrid>
  );
};

export default AdminSearchCompaniesResultsTable;

const EllipsedCell = styled.div`
  display: flex;
  align-items: center;
  line-height: 1.2;
`;
const EllipsedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
`;

const OverlayEntireCell = styled.div`
  td:has(&&) {
    position: relative;
  }
`;

const CellLink = styled(Link)`
  z-index: 0;
  position: absolute;
  inset: 0;
`;

const ArchivedCircle = styled.span`
  z-index: 1;
  flex-shrink: 0;
  position: relative;
  margin-left: 8px;
  transform: translateY(2px);

  && i {
    color: ${(props) => props.theme.colors.statusLabelYellowText};
    font-size: 14px;
    position: relative;
  }

  &&::before {
    content: '';
    position: absolute;
    display: block;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: ${(props) => props.theme.colors.statusLabelYellowBg};
    --offset: calc((22px - 14px) / -2);
    transform: translate(var(--offset), var(--offset));
  }
`;

const PlusXMore = styled.span`
  display: inline-block;
  margin-left: 4px;
  color: ${(props) => props.theme.colors.primaryOrange};
`;
